import { DetectedMeeting } from '@highlight/common'
import { useIsTakingNotes } from '@renderer/hooks/audio/use-audio-note-selectors'
import { useCurrentMeeting } from '@renderer/hooks/useCurrentMeeting'
import { useStartAudioNote } from '@renderer/hooks/useStartAudioNote'
import AsrIpcRenderer from '@renderer/ipc/AsrIpc.renderer'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useCallback, useEffect } from 'react'
import { Button } from '@highlight/ui'
import { useFeaturePrompt } from '@renderer/hooks/useFeaturePrompt'
import { useIncognitoMode } from '@renderer/hooks/useIncognitoMode'
import { Toast, useToast } from '../../index'

interface MeetingNotesFeaturePromptToastProps {
  meeting: DetectedMeeting
  toastId: string
}

export function MeetingNotesFeaturePromptToast({ meeting, toastId }: MeetingNotesFeaturePromptToastProps) {
  const { currentDetectedMeeting } = useCurrentMeeting()
  const isTakingNotes = useIsTakingNotes()
  const { startAudioNote } = useStartAudioNote()
  const { isIncognitoEnabled, disableIncognito } = useIncognitoMode()
  const { promptStatus } = useFeaturePrompt('meeting-notes')
  const toast = useToast()

  useEffect(() => {
    if (isTakingNotes && currentDetectedMeeting?.windowTitle === meeting.windowTitle) {
      toast.dismiss(toastId)
    }
  }, [toast, toastId, currentDetectedMeeting?.windowTitle, meeting.windowTitle])

  const onStart = useCallback(
    async (e: React.MouseEvent) => {
      e.stopPropagation()
      if (isIncognitoEnabled) {
        await disableIncognito()
      }

      if (isTakingNotes) {
        await AsrIpcRenderer.saveConversation()
      }

      startAudioNote({ source: 'toast', meeting: meeting })
      AmplitudeRenderer.trackEvent('Feature Prompt Toast', {
        type: 'meeting-notes',
        action: 'enable',
        status: promptStatus,
      })

      AmplitudeRenderer.trackEvent('Audio Note Action', {
        action: 'start taking notes',
        type: 'detected meeting feature prompt',
      })
      toast.dismiss(toastId)
    },
    [toast, toastId, isTakingNotes, startAudioNote, meeting, promptStatus, isIncognitoEnabled, disableIncognito],
  )

  const description = isIncognitoEnabled
    ? 'Starting this transcription will turn off incognito mode'
    : isTakingNotes
      ? 'Save your current note and start a new note?'
      : 'Take notes with Highlight'

  return (
    <Toast toastId={toastId}>
      <Toast.Content>
        <Toast.Footer>
          <span>{description}</span>
          <Button onClick={onStart} size="sm" variant="primary">
            {isIncognitoEnabled ? 'Start Transcribing' : 'Take Notes'}
          </Button>
        </Toast.Footer>
      </Toast.Content>
    </Toast>
  )
}
